<template>
    <VDialog v-model="dialog" max-width="600">
        <VCard flat :style="backgroundStyle">
            <VCardTitle class="d-flex align-center mb-2">
                <span class="text-h6 font-weight-medium">Add funds</span>
                <VSpacer />
                <VIcon icon="$mdiClose" rounded @click="dialog = false" />
            </VCardTitle>

            <VCardText>
                <div class="w-100 text-body-1 text-medium-emphasis mb-12">
                    Your current balance is <span class="font-weight-medium text-primary">
                        £{{ userData.user.balance.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        }) }}
                    </span>.
                </div>

                <VTextField v-model="amount" label="Amount" type="number" :min="minimum" :max="maximum" width="240"
                    color="primary" variant="solo" flat prefix="£"
                    :rules="[value => value >= minimum && value <= maximum || `Value must be greater than or equal to £${minimum} and less than or equal to £${maximum}`]">
                </VTextField>

                <div class="w-75 text-body-2 text-medium-emphasis mb-4">
                    A new invoice will be created.
                </div>
            </VCardText>
            <VCardActions class="d-flex justify-center">
                <VBtn width="200" color="primary" @click="addFunds(amount); dialog = false"
                    :variant="theme.global.name.value === 'dark' ? 'tonal' : 'flat'">
                    Add funds
                </VBtn>
            </VCardActions>
        </VCard>
    </VDialog>


    <VMenu :style="`font-family: ${store.globalFont} !important;`" :closeOnContentClick="false"
        v-model="link.secureAccountMenu">
        <template v-slot:activator="{ props }">
            <VBtn class="ml-2" color="medium-emphasis" v-bind="props" icon size="small">
                <VIcon size="x-large" :icon="link.secureAccountMenu ? '$mdiAccount' : '$mdiAccountOutline'" />
            </VBtn>
        </template>
        <VCard :style="backgroundStyle" min-width="300">
            <VList color="primary" style="background: transparent;" class="pa-0">
                <VListItem lines="two">
                    <template #prepend>
                        <VAvatar v-if="userData.user.profilePicture"
                            :image="`/assets/images/users/${userData.user.profilePicture}`" size="large" class="mr-2" />
                        <VAvatar v-else-if="userData.user.role === 'admin'" class="mr-2"
                            :color="formatColour(userData.user.role)">
                            {{ prettyWords(userData.user.firstName.charAt(0)) }}
                        </VAvatar>
                        <VAvatar v-else class="mr-2" :color="formatColour(userData.user.role)">
                            {{ prettyWords(userData.user.firstName.charAt(0)) }}
                        </VAvatar>
                    </template>
                    <div class="d-flex align-center font-weight-medium text-high-emphasis">
                        {{ prettyWords(userData.user.firstName) }} {{ prettyWords(userData.user.lastName) }}
                    </div>
                    <div class="d-flex align-center font-weight-medium text-medium-emphasis">
                        <VChip :color="formatColour(userData.user.role)" size="small" density="comfortable"
                            class="font-weight-medium text-overline" style="letter-spacing: 0.05rem !important"
                            :variant="theme.global.name.value === 'dark' ? 'tonal' : 'flat'">
                            {{ prettyWords(userData.user.role) }}
                        </VChip>
                    </div>
                </VListItem>

                <VDivider />

                <VListItem v-if="false" link @click="dialog = true">
                    <template #prepend>
                        <VIcon icon="$mdiBank" />
                    </template>
                    <VListItemTitle>
                        Add funds
                    </VListItemTitle>
                    <VListItemSubtitle class="font-weight-medium">
                        {{ store.sensitivityMode ? '???????' :
                            `£${userData.user.balance.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}` }}
                    </VListItemSubtitle>
                </VListItem>

                <!-- Updated: support either `to` or `click` per item -->
                <template v-for="(item, index) in menuItems" :key="index">
                    <VListItem :link="true" :to="item.to" @click="handleMenuItem(item)">
                        <template #prepend>
                            <VIcon :icon="item.icon" />
                        </template>
                        <VListItemTitle>
                            {{ item.title }}
                        </VListItemTitle>
                    </VListItem>
                </template>
            </VList>
        </VCard>
    </VMenu>
</template>

<script setup lang="ts">
import { useAppLink } from '@S/appLink';
import { useAppStore } from '@S/appStore';
import { useUserDataStore } from '@S/userDataStore';
import { useTheme } from 'vuetify';
import { useBackgroundStyle } from '@R/constants/componentStyle';
import { useAuth } from '@R/composables/useAuth';
import { formatColour } from '@R/utils/formatData';

let store = useAppStore();
let link = useAppLink();
let userData = useUserDataStore();

const { authToken } = useACookie();
const theme = useTheme();
const backgroundStyle = useBackgroundStyle();
const notifier = useNotifier();

const dialog = ref(false);
const amount = ref(5.00);
const minimum = ref(5.00);
const maximum = ref(500.00);

async function addFunds(amount: number) {
    if (amount < minimum.value || amount > maximum.value) {
        notifier.toast({
            icon: '$mdiAlert',
            type: 'error',
            title: 'Invalid Amount',
            text: `Amount must be between £${minimum.value} and £${maximum.value}.`,
            color: 'orange',
            onClick: () => { },
            onClose: () => { }
        });
        return;
    }

    try {
        const { data: response, error } = await useFetch('/api/v1/invoices/create', {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken.value}` },
            body: { amount }
        });

        if (error.value) throw new Error(error.value.message);

        if (response.value?.statusCode === 200 && 'amount' in response.value && 'invoiceId' in response.value) {
            notifier.toast({
                icon: '$mdiCheck',
                type: 'success',
                title: 'Invoice Created',
                text: `Invoice created for £${response.value.amount}.`,
                color: 'green',
                onClick: () => { },
                onClose: () => { }
            });

            navigateTo(`/invoices/${response.value.invoiceId}`);
        } else {
            notifier.toast({
                icon: '$mdiAlert',
                type: 'error',
                title: 'Invoice Failed',
                text: response.value?.statusMessage ?? 'Unable to create invoice.',
                color: 'red',
                onClick: () => { },
                onClose: () => { }
            });
        }
    } catch (error: any) {
        console.error('Add Funds Error:', error);
        notifier.toast({
            icon: '$mdiAlert',
            type: 'error',
            title: 'Error',
            text: error.message,
            color: 'red',
            onClick: () => { },
            onClose: () => { }
        });
    }
}

/** ─────────────────────────────────────────────────────────
 *  Menu items now support either `to` (navigate) OR `click` (run fn)
 *  ───────────────────────────────────────────────────────── */
type MenuItem =
    | { title: string; icon: string; to: string; click?: never }
    | { title: string; icon: string; click: () => Promise<void> | void; to?: never }

const { logout } = useAuth()

const menuItems: MenuItem[] = [
    { title: 'Account', to: '/account', icon: '$mdiAccount' },
    // { title: 'Activity', to: '/actions', icon: '$mdiViewList' },
    // Logout uses click handler and NO `to`
    { title: 'Logout', click: () => logout({ redirectTo: '/auth/login' }), icon: '$mdiLogout' },
]

// Single handler so the template stays simple & visuals untouched
async function handleMenuItem(item: MenuItem) {
    try {
        if ('click' in item && typeof item.click === 'function') {
            await item.click()
        } else if ('to' in item && item.to) {
            await navigateTo(item.to)
        }
    } finally {
        link.secureAccountMenu = false // close menu after action
    }
}

</script>
